import React from "react";
//#region types
import {
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
  FormItemDefinitionType,
} from "models/propTypes/genericFormProps";
import { RevenueReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants } from "utils/constants";
import { TagsFormProps } from "models/propTypes/dimensionPropTypes";
import { FormikProps } from "formik";
//#endregion
//#region components
import { GenericForm } from "components/common";
//#endregion
import { getWholeReportDimensions, seedTagRowsFromWholeReport } from "./tagsHelper";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

type TagRowsFieldName = "reportRows" | "salesRows" | "paymentRows" | "otherRows";

const TagsForm: React.ForwardRefRenderFunction<FormHandles, TagsFormProps> = (props, ref) => {
  const { otherHeadingKey, translateDimensionLabels, ...formProps } = props;

  const tagRowFields = {
    description: {
      label: "",
      type: FormEnums.FormFieldType.TEXT,
      readonly: true,
      widthColumnCount: 4,
    },
    dimensions: {
      label: "",
      type: FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR,
      widthColumnCount: 8,
      extraProps: {
        type: DimensionEnums.DimensionCategory.CashReconciliation,
        translateDimensionLabels,
      },
    },
  } as FormFieldDefinitionType<RevenueReportingFormTypes.EditTagsRow>;

  const tableExtraProps = {
    hideActions: true,
    isCompactTable: true,
    idFieldKey: FormFieldConstants.RevenueReporting.EditTagsRow.Id,
  } as EditableTableFieldProps<RevenueReportingFormTypes.EditTagsRow>;

  const sectionClassName = clsx(cssExports.revenueReportingFormSection, cssExports.noBorder);

  const onEditInRowsChange = (
    name: string,
    editInRows: boolean,
    formikProps: FormikProps<RevenueReportingFormTypes.EditTags>
  ) => {
    if (!editInRows) {
      return;
    }

    const { values, setValues } = formikProps;
    const wholeReportDimensions = getWholeReportDimensions(values.reportRows);
    if (!wholeReportDimensions.length) {
      return;
    }

    setValues({
      ...values,
      salesRows: seedTagRowsFromWholeReport(values.salesRows, wholeReportDimensions),
      paymentRows: seedTagRowsFromWholeReport(values.paymentRows, wholeReportDimensions),
      otherRows: seedTagRowsFromWholeReport(values.otherRows, wholeReportDimensions),
    });
  };

  const getRowsSection = (
    fieldName: TagRowsFieldName,
    label: string,
    hidden: (values: RevenueReportingFormTypes.EditTags) => boolean
  ) => ({
    label,
    type: FormEnums.FormSectionType.FIELDGROUPSECTION,
    className: sectionClassName,
    hidden,
    innerFields: {
      [fieldName]: {
        label: "",
        type: FormEnums.FormFieldType.EDITABLETABLE,
        innerFields: tagRowFields,
        extraProps: tableExtraProps,
      },
    } as FormFieldDefinitionType<RevenueReportingFormTypes.EditTags>,
  });

  return (
    <GenericForm<RevenueReportingFormTypes.EditTags>
      formRef={ref}
      {...formProps}
      fields={
        [
          getRowsSection("reportRows", "", (values) => !!values.editInRows),
          getRowsSection(
            "salesRows",
            TranslationKeys.Common.Sale,
            (values) => !values.editInRows || !values.salesRows?.length
          ),
          getRowsSection(
            "paymentRows",
            TranslationKeys.Common.PaymentMethod,
            (values) => !values.editInRows || !values.paymentRows?.length
          ),
          getRowsSection("otherRows", otherHeadingKey, (values) => !values.editInRows || !values.otherRows?.length),
          {
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            className: clsx(sectionClassName, cssExports.revenueReportingTagsModeToggle),
            innerFields: {
              editInRows: {
                label: TranslationKeys.Common.EditInRRows,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onEditInRowsChange,
              },
            } as FormFieldDefinitionType<RevenueReportingFormTypes.EditTags>,
          },
        ] as FormItemDefinitionType<RevenueReportingFormTypes.EditTags>
      }
      hideSuccess
      formGridLayout={{ message: false }}
    />
  );
};

export default React.forwardRef(TagsForm);
